/**
 * Web port of mobile utils/heatmapGrid.ts — byte-identical logic.
 *
 * "Your days" grid for Progress (ported from KidneyPal 4.36's day heatmap).
 *
 * A GitHub-style grid: 7 rows (Mon → Sun), one column per week, the newest
 * week on the right. Every day is coloured by its day score (budget status,
 * LIVER_MODEL_SPEC §4a) — the caller supplies the scored day, this module
 * never resolves limits itself and never writes anything.
 *
 * PURE MODULE — no React / Firebase / date-library imports. Dates are
 * 'YYYY-MM-DD' strings and all arithmetic runs on UTC midnights, so a DST
 * change can never shift a day into the wrong row.
 */

import type { DayScore } from '@/diary/today-score';

/** Smallest cell edge (pt). The column count is whatever fits at this size. */
export const HEATMAP_MIN_CELL = 12;
/** Gap between cells. */
export const HEATMAP_GAP = 3;
/** Never more than half a year of columns (tablet). */
export const HEATMAP_MAX_WEEKS = 26;

const DAY_MS = 86400000;

function isoToUtc(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function utcToIso(ms: number): string {
  const dt = new Date(ms);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 'YYYY-MM-DD' plus n calendar days (n may be negative). */
export function addDaysIso(date: string, n: number): string {
  return utcToIso(isoToUtc(date) + n * DAY_MS);
}

/** Row index of a date: Monday = 0 … Sunday = 6. */
export function weekdayRow(date: string): number {
  return (new Date(isoToUtc(date)).getUTCDay() + 6) % 7;
}

/** How many week columns fit in `width` at the minimum cell size. 0 until measured. */
export function heatmapWeeksForWidth(
  width: number,
  minCell: number = HEATMAP_MIN_CELL,
  gap: number = HEATMAP_GAP,
  maxWeeks: number = HEATMAP_MAX_WEEKS,
): number {
  if (!(width > 0)) return 0;
  return Math.max(1, Math.min(maxWeeks, Math.floor((width + gap) / (minCell + gap))));
}

/** First (a Monday) and last (a Sunday) date the grid shows. */
export function heatmapRange(today: string, weeks: number): { start: string; end: string } {
  const monday = addDaysIso(today, -weekdayRow(today));
  return { start: addDaysIso(monday, -7 * (Math.max(1, weeks) - 1)), end: addDaysIso(monday, 6) };
}

/**
 * safe / moderate / high — the day score's band (≤30 / 31–60 / >60)
 * drinks — only zero-calorie drinks logged: nothing the day score judges
 *          (the diary hero shows no score either)
 * empty  — nothing logged
 * locked — free user, older than the diary's free window
 * future — after today (blank)
 */
export type HeatmapCellState = 'safe' | 'moderate' | 'high' | 'drinks' | 'empty' | 'locked' | 'future';

export interface HeatmapCell {
  date: string;
  /** Week column, 0 = oldest. */
  col: number;
  /** Monday = 0 … Sunday = 6. */
  row: number;
  state: HeatmapCellState;
  isToday: boolean;
  /** The judged day's score (null for every other state). */
  score: DayScore | null;
}

/** One day's inputs: how many entries it holds, and its day score (null = nothing scoreable). */
export interface HeatmapDay {
  totalItems: number;
  score: DayScore | null;
}

export interface HeatmapInput {
  weeks: number;
  today: string;
  /** A stored day (null / undefined = no doc = nothing logged). */
  dayFor: (date: string) => HeatmapDay | null | undefined;
  isLocked: (date: string) => boolean;
  /** When the diary is showing today, today's cell comes from the live diary instead. */
  liveToday?: HeatmapDay | null;
}

export interface HeatmapGrid {
  weeks: number;
  start: string;
  end: string;
  /** columns[col][row] */
  columns: HeatmapCell[][];
  /** Every non-future cell, oldest first — the order the selection steps through. */
  days: HeatmapCell[];
  /** Finished (not today), unlocked days judged safe. */
  safeDays: number;
}

/**
 * A day's colour state from its day score — the one judge behind both the
 * "Your days" cells and the week card's dots (Progress), so they never disagree.
 */
export function judgeDay(day: HeatmapDay | null | undefined): { state: HeatmapCellState; score: DayScore | null } {
  if (!day || !(day.totalItems > 0)) return { state: 'empty', score: null };
  if (!day.score) return { state: 'drinks', score: null };
  return { state: day.score.riskLevel, score: day.score };
}

export function buildHeatmapGrid(input: HeatmapInput): HeatmapGrid {
  const weeks = Math.max(1, Math.floor(input.weeks));
  const { start, end } = heatmapRange(input.today, weeks);
  const columns: HeatmapCell[][] = [];
  const days: HeatmapCell[] = [];
  let safeDays = 0;
  for (let col = 0; col < weeks; col++) {
    const column: HeatmapCell[] = [];
    for (let row = 0; row < 7; row++) {
      const date = addDaysIso(start, col * 7 + row);
      const isToday = date === input.today;
      let state: HeatmapCellState;
      let score: DayScore | null = null;
      if (date > input.today) {
        state = 'future';
      } else if (!isToday && input.isLocked(date)) {
        state = 'locked';
      } else if (isToday && input.liveToday) {
        ({ state, score } = judgeDay(input.liveToday));
      } else {
        ({ state, score } = judgeDay(input.dayFor(date)));
      }
      const cell: HeatmapCell = { date, col, row, state, isToday, score };
      column.push(cell);
      if (state !== 'future') days.push(cell);
      // Finished days only: today's colour is "so far" and can still change.
      if (state === 'safe' && !isToday) safeDays += 1;
    }
    columns.push(column);
  }
  return { weeks, start, end, columns, days, safeDays };
}
